import { useEffect, useLayoutEffect } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { LayoutDashboard, Video, FileText, Folder, Users, Globe, CircleAlert, Settings, Lock, ExternalLink, FlaskConical, Bug } from 'lucide-react'
import { useConfig, useContentReadOnly } from '@/hooks/useConfig'

export function AdminLayout() {
  const location = useLocation()
  const contentReadOnly = useContentReadOnly()
  const { data: config } = useConfig()

  // 어드민은 Tailwind 기본 글자 크기를 유지한다 (globals.css 의 :root[data-area="admin"]).
  // 첫 페인트 전에 적용되도록 layout effect 를 쓴다.
  useLayoutEffect(() => {
    document.documentElement.dataset.area = 'admin'
    return () => {
      delete document.documentElement.dataset.area
    }
  }, [])

  useEffect(() => {
    document.title = '빅스젠 어드민'

    return () => {
      document.title = '빅스젠 - AI 비디오 생성기'
    }
  }, [])

  const navItems = [
    { path: '/admin', label: '대시보드', icon: LayoutDashboard },
    { path: '/admin/generations', label: '생성된 비디오', icon: Video },
    { path: '/admin/failed-videos', label: '실패한 비디오', icon: CircleAlert },
    { path: '/admin/shared-videos', label: '공개된 비디오', icon: Globe },
    { path: '/admin/templates', label: '템플릿', icon: FileText },
    { path: '/admin/categories', label: '템플릿 카테고리', icon: Folder },
    { path: '/admin/users', label: '사용자', icon: Users },
    { path: '/admin/errors', label: '오류 리포트', icon: Bug },
    { path: '/admin/settings', label: '설정', icon: Settings },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-6 py-4">
          <Link
            to="/admin"
            className="text-xl font-bold tracking-tight text-heading hover:text-primary transition-colors"
          >
            빅스젠 어드민
          </Link>
          <Link
            to="/"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-800 transition-all duration-200"
            aria-label="서비스로 가기"
            title="서비스로 가기"
          >
            <ExternalLink className="w-[18px] h-[18px]" />
          </Link>
        </div>
      </header>

      {config?.isStaging && (
        <div className="border-b border-amber-300 bg-amber-50 px-3 py-2 md:px-6">
          <div className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
            <FlaskConical className="h-3.5 w-3.5" />
            STAGING — 검증용 환경입니다
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row">
        {/* Sidebar */}
        <aside className="w-full flex-shrink-0 border-b border-gray-200 bg-white md:min-h-[calc(100vh-73px)] md:w-64 md:border-r md:border-b-0">
          <nav aria-label="관리자 메뉴" className="flex gap-1 overflow-x-auto p-4 md:block md:space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(`${item.path}/`))
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  className={`flex shrink-0 items-center space-x-3 px-4 py-2.5 rounded-lg transition-all ${
                    isActive
                      ? 'bg-primary text-white font-semibold shadow-sm'
                      : 'text-caption hover:bg-gray-50 hover:text-body font-medium'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-[15px] leading-snug">{item.label}</span>
                </Link>
              )
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="min-w-0 flex-1 p-4 md:p-8">
          {contentReadOnly && (
            <div className="mb-6 flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <Lock className="w-4 h-4 flex-shrink-0" />
              <span>
                이 배포는 콘텐츠 읽기 전용입니다. 템플릿·카테고리·설정(장애 공지 제외)은 wrks(운영) 관리자에서만 수정할 수 있으며, 여기서 변경해도 적용되지 않습니다. 장애 공지는 배포마다 따로 관리합니다.
              </span>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  )
}
